import type {SwadeSkillInterface} from "@/types/items";
import type {WorldInterface} from "@/types/worlds";
import type {AttributeNameType} from "@/types/characters";
import type {SystemInterface} from "@/types/systems";
import type { FilterDescriptor } from '@progress/kendo-data-query'
import {defineStore} from "pinia";
import { FilterOperator } from '@progress/kendo-data-query'
import {useAuthDataStore} from "@/stores/auth-data";
import {useUxData} from "@/stores/ux-data";
import {
    buildSkillPredicates,
    buildEdgePredicates,
    buildHindrancePredicates,
    buildSettingRulePredicates,
    buildWorldPredicates,
    type PredicateContext,
    type WorldPredicateContext,
} from "@/composables/buildPredicates";

const contextFromState = (state: any): PredicateContext => ({
    system: state.system,
    worlds: state.worlds,
    sources: state.sources,
    trashed: state.trashed,
});

// Premium worlds are left out for a user without premium access, except where a surface asks for the
// whole library: only the world list does, and it marks the premium worlds it can't use.
const worldContextFromState = (state: any, includePremium: boolean = false): WorldPredicateContext => ({
    system: state.system,
    trashed: state.trashed,
    ownedOnly: state.ownedOnly,
    excludePremium: !includePremium && !useAuthDataStore().hasPremiumAccess,
});

const sourcesFilter = (
    sources: Array<WorldInterface | null | undefined>,
): FilterDescriptor | null => {
    const uuids = sources.filter((s): s is WorldInterface => !!s).map((s) => s.uuid);
    return uuids.length
        ? { field: 'source.uuid', operator: FilterOperator.EqualTo, value: uuids }
        : null;
};

const classNamesFilter = (
    aliases: string[],
): FilterDescriptor | null => {
    if (!aliases?.length) return null;
    const ux = useUxData();
    const classNames = aliases
        .map((alias) => ux.labels.elements.aliases[alias])
        .filter((name): name is string => !!name);
    return classNames.length
        ? { field: 'element.className', operator: FilterOperator.Contains, value: classNames }
        : null;
};


export const usePredicateStore = defineStore('predicateStore', {
    state: () => ({
        system: null as SystemInterface|null,
        worlds: [] as WorldInterface[],
        sources: [] as WorldInterface[],
        ownedOnly: true as boolean,
        trashed: false as boolean,
        // The session page size of the element browsers and the Data Management lists: null sends
        // no `limit`, so the server uses its own size for the context; one of PAGE_SIZES sends
        // `limit=<n>` (spec 2026-10-16).
        pageSize: null as number | null,
        systemModFilters: {
            stringPartial: "" as string,
        },
        worldFilters: {
            stringPartial: "" as string,
        },
        elementFilters: {
            stringPartial: "" as string,
            alias: "" as string,
            types: [] as string[],
        },
        characterFilters: {
            stringPartial: "" as string,
            // The character-LIST world filter. Deliberately NOT `state.worlds`: that slot is
            // element scoping and is written on mount by finders and editors, which used to
            // re-filter this list behind the user's back.
            worlds: [] as WorldInterface[],
            advances: {
                min: null as number | null,
                max: null as number | null,
            },
        },
        skillFilters: {
            stringPartial: "" as string,
            skills: [] as SwadeSkillInterface[],
            linkedAttributes: [] as AttributeNameType[],
        },
        abilityFilters: {
            stringPartial: "" as string,
            types: [] as string[],
        },
        edgeFilters: {
            stringPartial: "" as string,
            types: [] as string[],
        },
        hindranceFilters: {
            stringPartial: "" as string,
            severity: [] as string[],
        },
        itemFilters: {
            stringPartial: "" as string,
            types: [] as string[],
        },
        notificationFilters: {
            stringPartial: "" as string,
        },
    }),
    getters: {
        // The rule grid's filters: systemModFilters.stringPartial is the keyword its own search box
        // writes, and nothing else writes it.
        systemModPredicates: (state: any): FilterDescriptor[] =>
            buildSettingRulePredicates(contextFromState(state), state.systemModFilters.stringPartial),
        // The same filters for a keyword of the caller's own. A rule type-ahead looks rules up with
        // the text typed into it, which never goes into the slot the grid filters by.
        systemModPredicatesFor: (state: any) => (stringPartial: string): FilterDescriptor[] =>
            buildSettingRulePredicates(contextFromState(state), stringPartial),
        // A world surface's filters (the world list, the guided sources step, a world browser):
        // worldFilters.stringPartial is the keyword that surface's own search box writes.
        worldPredicates: (state: any): FilterDescriptor[] =>
            buildWorldPredicates(worldContextFromState(state), state.worldFilters.stringPartial),
        // The same filters for a keyword of the caller's own: a world type-ahead's. The world list
        // passes includePremium, the one surface that shows premium worlds to everyone.
        worldPredicatesFor: (state: any) => (stringPartial: string, options: { includePremium?: boolean } = {}): FilterDescriptor[] =>
            buildWorldPredicates(worldContextFromState(state, options.includePremium), stringPartial),
        notificationPredicates: (state: any) => {
            // console.debug(state);
            let filters: Array<FilterDescriptor> = [];
            if (state.notificationFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'notification.content',
                    operator: FilterOperator.Contains,
                    value: state.notificationFilters.stringPartial
                })
            }

            return filters;
        },
        elementPredicates: (state: any) => {
            let filters: Array<FilterDescriptor> = [];
            if (state.system !== null) {
                filters.push({ field: 'system.uuid', operator: FilterOperator.EqualTo, value: state.system.uuid })
            }

            if (state.worlds.filter((item?: WorldInterface|null) => item).length > 0) {
                filters.push({
                    field: 'world.uuid',
                    operator: FilterOperator.EqualTo,
                    value: state.worlds
                        .filter((item?: WorldInterface|null) => item)
                        .map((item: WorldInterface) => item.uuid)
                })
            }

            const sourcesPredicate = sourcesFilter(state.sources);
            if (sourcesPredicate) filters.push(sourcesPredicate);

            if (state.elementFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'element.name',
                    operator: FilterOperator.Contains,
                    value: state.elementFilters.stringPartial
                })
            }

            if (state.elementFilters.alias.length > 0 && state.elementFilters.alias !== 'all') {
                const ux = useUxData();
                filters.push({
                    field: 'element.className',
                    operator: FilterOperator.Contains,
                    value: ux.labels.elements.aliases[state.elementFilters.alias ?? ''] ?? '',
                })
            }

            if (state.elementFilters.alias === 'all') {
                const classNames = classNamesFilter(state.elementFilters.types);
                if (classNames) filters.push(classNames);
            }

            if (state.trashed) {
                filters.push({ field: 'element.dateDeleted', operator: FilterOperator.IsNotNull, value: state.trashed })
                filters.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
            }

            if (state.ownedOnly) {
                filters.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
            }

            return filters;
        },
        associationPredicates: (state: any) => {
            let filters: Array<FilterDescriptor> = [];
            if (state.elementFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'element.name',
                    operator: FilterOperator.Contains,
                    value: state.elementFilters.stringPartial
                })
            }

            const classNames = classNamesFilter(state.elementFilters.types);
            if (classNames) filters.push(classNames);

            return filters;
        },
        characterPredicates: (state: any) => {
            // console.debug(state);
            let filters: Array<FilterDescriptor> = [];
            if (state.system !== null) {
                filters.push({ field: 'system.uuid', operator: FilterOperator.EqualTo, value: state.system.uuid })
            }

            if (state.characterFilters.worlds.filter((item?: WorldInterface|null) => item).length > 0) {
                filters.push({
                    field: 'world.uuid',
                    operator: FilterOperator.EqualTo,
                    value: state.characterFilters.worlds
                        .filter((item?: WorldInterface|null) => item)
                        .map((item: WorldInterface) => item.uuid) })
            }

            if (state.characterFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'character.name',
                    operator: FilterOperator.Contains,
                    value: state.characterFilters.stringPartial
                })
            }

            if (state.characterFilters.advances.min) {
                filters.push({ field: 'swade.advancements', operator: FilterOperator.GreaterThanOrEqual, value: state.characterFilters.advances.min })
            }

            if (state.characterFilters.advances.max) {
                filters.push({ field: 'swade.advancements', operator: FilterOperator.LessThanOrEqual, value: state.characterFilters.advances.max })
            }

            if (state.trashed) {
                filters.push({ field: 'character.dateDeleted', operator: FilterOperator.IsNotNull, value: state.trashed })
            }

            return filters;
        },
        skillPredicates: (state: any) => {
            return buildSkillPredicates(contextFromState(state), {
                stringPartial: state.skillFilters.stringPartial,
                skills: state.skillFilters.skills,
                linkedAttributes: state.skillFilters.linkedAttributes,
            });
        },
        abilityPredicates: (state: any) => {
            // console.debug(state);
            let filters: Array<FilterDescriptor> = [];
            if (state.system !== null) {
                filters.push({ field: 'system.uuid', operator: FilterOperator.EqualTo, value: state.system.uuid })
            }

            if (state.worlds.length > 0) {
                filters.push({ field: 'world.uuid', operator: FilterOperator.EqualTo, value: state.worlds.map((item: WorldInterface) => item.uuid) })
            }

            const sourcesPredicate = sourcesFilter(state.sources);
            if (sourcesPredicate) filters.push(sourcesPredicate);

            if (state.abilityFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'element.name',
                    operator: FilterOperator.Contains,
                    value: state.abilityFilters.stringPartial
                })
            }

            if (state.abilityFilters.types.length > 0) {
                filters.push({ field: 'ability.type', operator: FilterOperator.EqualTo, value: state.abilityFilters.types })
            }

            if (state.trashed) {
                filters.push({ field: 'element.dateDeleted', operator: FilterOperator.IsNotNull, value: state.trashed })
                filters.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
            }

            return filters;
        },
        edgePredicates: (state: any) => {
            return buildEdgePredicates(contextFromState(state), {
                stringPartial: state.edgeFilters.stringPartial,
                types: state.edgeFilters.types,
            });
        },
        hindrancePredicates: (state: any) => {
            return buildHindrancePredicates(contextFromState(state), {
                stringPartial: state.hindranceFilters.stringPartial,
                severity: state.hindranceFilters.severity,
            });
        },
        itemPredicates: (state: any) => {
            // console.debug(state);
            let filters: Array<FilterDescriptor> = [];
            if (state.system !== null) {
                filters.push({ field: 'system.uuid', operator: FilterOperator.EqualTo, value: state.system.uuid })
            }

            if (state.worlds.length > 0) {
                filters.push({ field: 'world.uuid', operator: FilterOperator.EqualTo, value: state.worlds.map((item: WorldInterface) => item.uuid) })
            }

            const sourcesPredicate = sourcesFilter(state.sources);
            if (sourcesPredicate) filters.push(sourcesPredicate);

            if (state.itemFilters.stringPartial.length > 0) {
                filters.push({
                    field: 'element.name',
                    operator: FilterOperator.Contains,
                    value: state.itemFilters.stringPartial
                })
            }

            if (state.itemFilters.types.length > 0) {
                filters.push({ field: 'item.type', operator: FilterOperator.EqualTo, value: state.itemFilters.types })
            }

            if (state.trashed) {
                filters.push({ field: 'element.dateDeleted', operator: FilterOperator.IsNotNull, value: state.trashed })
                filters.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
            }

            return filters;
        },
    },
    actions: {
        clear() {
            this.$reset();
        },
    }
})
